/* eslint-disable no-unused-vars */
import "./Messages.scss";
import { useState, useEffect } from "react";
import {
  InputAdornment,
  TextField,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { MessagesColumns } from "../../SourceData";
import ar_EG from "antd/es/locale/ar_EG"; // Arabic locale
import { ConfigProvider, DatePicker } from "antd";
import AddGif from "./../../assets/add-gif.gif";
import BlueButton from "../../components/BlueButton/BlueButton";
import ShowUser from "./../../assets/show-user.svg";
import api from "../../utils/api";
import dayjs from "dayjs";
import "dayjs/locale/ar"; // Import Arabic locale for dayjs
import { toast } from "react-toastify";
import { usePermissions } from "./../../hooks/usePermissions";

const { RangePicker } = DatePicker;

const Messages = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");

  // State variables
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [messageBalance, setMessageBalance] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Messages data state
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog state for viewing message details
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  dayjs.locale("ar");

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    // Reset to first page when filters change
    setPage(1);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Only update searchTerm if query is empty or has more than 2 characters
    if (value === "" || value.length > 2) {
      setSearchTerm(value);
      // Reset to first page when filters change
      setPage(1);
    }
  };

  // Handle add message button click
  const handleAddMessageClick = () => {
    // Check add permission
    if (!hasPermission(84)) {
      toast.error("ليس لديك صلاحية لإضافة الرسائل");
      return;
    }
  };

  // Handle view message click
  const handleViewMessageClick = (message) => {
    // Check view message permission
    if (!hasPermission(85)) {
      toast.error("ليس لديك صلاحية لعرض تفاصيل الرسالة");
      return;
    }

    setSelectedMessage(message);
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedMessage(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    return dayjs(dateString).format("DD/MM/YYYY - HH:mm");
  };

  // Get receiver type in Arabic
  const getReceiverTypeText = (type) => {
    switch (type) {
      case "specific-users":
        return "مستخدمين محددين";
      case "all-users":
        return "جميع المستخدمين";
      case "role-based":
        return "حسب الدور";
      default:
        return type;
    }
  };

  // Fetch messages data
  const fetchMessages = async () => {
    // Check view permission before fetching data
    if (!hasPermission(85)) {
      toast.error("ليس لديك صلاحية لعرض الرسائل");
      return;
    }

    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Add search term if it's not empty
      if (searchTerm) {
        params.q = searchTerm;
      }

      // Add date range parameters if selected
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format("YYYY-MM-DD");
        params.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      const response = await api.get("/messages", { params });

      if (response.data.status === "success") {
        setMessages(response.data.data);
        setTotalCount(response.data.result);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("فشل في جلب الرسائل. يرجى المحاولة مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch message balance
  const fetchMessageBalance = async () => {
    // Check balance permission before fetching data
    if (!hasPermission(83)) {
      return;
    }

    try {
      const response = await api.get("/messages/balance");
      if (response.data.status === "success") {
        setMessageBalance(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching message balance:", error);
      toast.error("فشل في جلب رصيد الرسائل. يرجى المحاولة مرة أخرى.");
    }
  };

  // Effect to fetch messages when dependencies change
  useEffect(() => {
    fetchMessages();
  }, [page, pageSize, searchTerm, dateRange]);

  // Effect to fetch message balance
  useEffect(() => {
    fetchMessageBalance();
  }, []);

  // Action column for DataGrid
  const actionColumn = [
    {
      field: "action",
      headerName: "",
      width: 160,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const canViewMessage = hasPermission(85);

        return (
          <div className="cell-action">
            <div
              className="cell-btn"
              onClick={() => handleViewMessageClick(params.row)}
              style={{
                cursor: canViewMessage ? "pointer" : "not-allowed",
                opacity: canViewMessage ? 1 : 0.5,
              }}
              title={
                canViewMessage
                  ? "عرض تفاصيل الرسالة"
                  : "ليس لديك صلاحية لعرض تفاصيل الرسالة"
              }
            >
              <img src={ShowUser} alt="عرض" />
            </div>
          </div>
        );
      },
    },
  ];

  // If user doesn't have view permission, show restricted access message
  if (!hasPermission(85)) {
    return (
      <div className="users">
        <div className="special-header">
          <p className="title">الرسائل النصية</p>
        </div>
        <div
          className="container"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
            backgroundColor: "white",
            borderRadius: "8px",
          }}
        >
          <p
            style={{
              fontSize: "18px",
              color: "#666",
              textAlign: "center",
            }}
          >
            ليس لديك صلاحية لعرض الرسائل
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="users">
      <div className="special-header">
        <div className="wrapper">
          <p className="title">
            الرسائل النصية{" "}
            <span className="count-badge">{totalCount || 0}</span>
          </p>

          {/* Only show balance if user has permission */}
          {hasPermission(83) && (
            <p className="balance">
              رصيد الرسائل المتبقية
              <span>{messageBalance || 0}</span>
            </p>
          )}
        </div>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="ابحث بعنوان الرسالة أو النص"
              onChange={handleSearchChange}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <ConfigProvider locale={ar_EG} direction="rtl">
              <RangePicker
                format={"DD MMM"}
                style={{
                  width: isMobile ? "100%" : isTablet ? "200px" : "300px",
                  padding: "0px 10px",
                }}
                placeholder={["من", "الي"]}
                className="range-picker"
                onChange={handleDateRangeChange}
                value={dateRange}
                popupStyle={{ direction: "rtl" }}
                inputReadOnly={true}
              />
            </ConfigProvider>
          </div>

          {/* Only show add button if user has permission */}
          {hasPermission(84) && (
            <BlueButton
              text="ارسال رسالة جديدة"
              size={isMobile ? "full" : isTablet ? "fit" : "large"}
              variant="fill"
              icon={<img src={AddGif} alt="" />}
              path="/messages/create"
              onClick={handleAddMessageClick}
            />
          )}
        </div>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={MessagesColumns.concat(actionColumn)}
          rows={messages.map((message, index) => ({
            ...message,
            // Generate unique id if not present
            id: message._id || index,
            // Add index for numbering
            index: (page - 1) * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            // Handle page change
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            // Handle page size change
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1); // Reset to first page when changing page size
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "رسالة في الصفحة",
            },
          }}
          rowHeight={80}
        />
      </div>

      {/* Message Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "1.5rem",
            color: "#1976d2",
            borderBottom: "1px solid #e0e0e0",
            paddingBottom: "16px",
          }}
        >
          تفاصيل الرسالة
        </DialogTitle>

        <DialogContent sx={{ padding: "24px" }}>
          {selectedMessage && (
            <Box>
              {/* Sender Information */}
              <Box sx={{ marginBottom: "20px" }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: "8px",
                    color: "#333",
                  }}
                >
                  المرسل
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#f5f5f5",
                    padding: "12px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Typography sx={{ fontWeight: "medium" }}>
                    {selectedMessage.sender?.name || "غير محدد"}
                  </Typography>
                  <Chip
                    label={
                      selectedMessage.sender?.role === "admin"
                        ? "مشرف"
                        : "مستخدم"
                    }
                    size="small"
                    color={
                      selectedMessage.sender?.role === "admin"
                        ? "primary"
                        : "default"
                    }
                  />
                </Box>
              </Box>

              <Divider sx={{ margin: "20px 0" }} />

              {/* Message Content */}
              <Box sx={{ marginBottom: "20px" }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: "8px",
                    color: "#333",
                  }}
                >
                  نص الرسالة
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#f8f9fa",
                    padding: "16px",
                    borderRadius: "8px",
                    border: "1px solid #e9ecef",
                    minHeight: "80px",
                  }}
                >
                  <Typography
                    sx={{
                      whiteSpace: "pre-wrap",
                      lineHeight: "1.6",
                      fontSize: "1rem",
                    }}
                  >
                    {selectedMessage.message}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ margin: "20px 0" }} />

              {/* Receiver Information */}
              <Box sx={{ marginBottom: "20px" }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: "8px",
                    color: "#333",
                  }}
                >
                  المستقبلين
                </Typography>

                <Box sx={{ marginBottom: "12px" }}>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      color: "#666",
                      marginBottom: "8px",
                    }}
                  >
                    نوع الإرسال:{" "}
                    {getReceiverTypeText(selectedMessage.receiverType)}
                  </Typography>
                </Box>

                {selectedMessage.receivers &&
                  selectedMessage.receivers.length > 0 && (
                    <Box
                      sx={{
                        backgroundColor: "#f5f5f5",
                        padding: "12px",
                        borderRadius: "8px",
                        maxHeight: "200px",
                        overflowY: "auto",
                      }}
                    >
                      {selectedMessage.receivers.map((receiver, index) => (
                        <Box
                          key={receiver._id || index}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 0",
                            borderBottom:
                              index < selectedMessage.receivers.length - 1
                                ? "1px solid #e0e0e0"
                                : "none",
                          }}
                        >
                          <Box>
                            <Typography sx={{ fontWeight: "medium" }}>
                              {receiver.receiverId?.name || "غير محدد"}
                            </Typography>
                            {receiver.receiverId?.phoneNumber && (
                              <Typography
                                sx={{
                                  fontSize: "0.85rem",
                                  color: "#666",
                                  direction: "ltr",
                                  textAlign: "left",
                                }}
                              >
                                {receiver.receiverId.phoneNumber}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            label={
                              receiver.receiverId?.role === "admin"
                                ? "مشرف"
                                : "مستخدم"
                            }
                            size="small"
                            color={
                              receiver.receiverId?.role === "admin"
                                ? "primary"
                                : "default"
                            }
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
              </Box>

              <Divider sx={{ margin: "20px 0" }} />

              {/* Creation Date */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: "8px",
                    color: "#333",
                  }}
                >
                  تاريخ الإرسال
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#e3f2fd",
                    padding: "12px",
                    borderRadius: "8px",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "medium",
                      direction: "ltr",
                      textAlign: "center",
                    }}
                  >
                    {formatDate(selectedMessage.createdAt)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: "16px 24px",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button
            onClick={handleCloseDialog}
            variant="contained"
            sx={{
              minWidth: "100px",
              borderRadius: "8px",
            }}
          >
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Messages;
